import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Card, Row, Col, Alert, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { adminClientAPI } from '../api/api';
import './ClientDashboard.css';

/**
 * Client Dashboard Component
 * Lists all clients with management capabilities
 */
const ClientDashboard = ({ showCreateForm, onCreateClick }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // view, edit
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First verify session is still valid
      const { authAPI } = await import('../api/api');
      const sessionCheck = await authAPI.checkSession();
      
      if (!sessionCheck.data.success || !sessionCheck.data.user) {
        throw new Error('Session expired. Please login again.');
      }
      
      // Check if user has Admin role
      const userRole = sessionCheck.data.user.Role || sessionCheck.data.user.role || '';
      if (userRole !== 'Admin') {
        throw new Error('Admin access required. Your role: ' + userRole);
      }
      
      const response = await adminClientAPI.getAllClients();
      
      if (response.data && response.data.success) {
        setClients(response.data.data || []);
        setError(null);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch clients');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load clients';
      setError(errorMsg);
      
      // If 401 or 403, show specific message
      if (err.response?.status === 401) {
        setError('Session expired. Please refresh the page and login again.');
      } else if (err.response?.status === 403) {
        setError('Admin access required. You do not have permission to access this page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setEditData(client);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setEditData({ ...client });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      // Convert field names from PascalCase to camelCase for backend
      const convertedData = {
        companyName: editData.CompanyName,
        industry: editData.Industry,
        description: editData.Description,
        contactName: editData.ContactName,
        contactTitle: editData.ContactTitle,
        contactEmail: editData.ContactEmail,
        contactPhone: editData.ContactPhone,
        address1: editData.Address1,
        address2: editData.Address2,
        city: editData.City,
        state: editData.State,
        zip: editData.Zip,
        country: editData.Country,
        billingContactName: editData.BillingContactName,
        billingContactEmail: editData.BillingContactEmail,
        billingContactPhone: editData.BillingContactPhone,
        supportContactName: editData.SupportContactName,
        supportContactEmail: editData.SupportContactEmail,
        supportContactPhone: editData.SupportContactPhone
      };

      const response = await adminClientAPI.updateClient(selectedClient.ClientID, convertedData);

      if (response.data && response.data.success) {
        // Refresh client list
        await fetchClients();
        setShowModal(false);
        alert('Client updated successfully');
      } else {
        throw new Error(response.data?.message || 'Failed to update client');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'ACTIVE': 'success',
      'INACTIVE': 'danger',
      'TRIAL': 'warning',
      'SUSPENDED': 'danger',
      'CANCELED': 'dark'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <Container className="client-dashboard py-4">
      <Row className="mb-4">
        <Col>
          <h2>Client Management Dashboard</h2>
          <p className="text-muted">Manage all client accounts and settings</p>
        </Col>
        <Col md="auto">
          <Button variant="success" size="lg" onClick={onCreateClick}>
            + Create New Client
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading clients...</p>
        </div>
      ) : clients.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <h5>No clients found</h5>
            <p className="text-muted mb-3">Get started by creating your first client</p>
            <Button variant="success" onClick={onCreateClick}>
              Create First Client
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Company</th>
                <th>Contact</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(client => (
                <tr key={client.ClientID}>
                  <td>
                    <strong>{client.CompanyName}</strong>
                    <br />
                    <small className="text-muted">{client.CompanyCode}</small>
                  </td>
                  <td>
                    {client.ContactName}
                    <br />
                    <small>{client.ContactEmail}</small>
                  </td>
                  <td>
                    <small>{new Date(client.CreatedDateTime).toLocaleDateString()}</small>
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleViewClient(client)}
                      className="me-1"
                    >
                      View
                    </Button>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => handleEditClient(client)}
                      className="me-1"
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* View/Edit Modal */}
      <Modal show={showModal && modalMode !== 'settings'} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'view' ? 'View Client' : 'Edit Client'} - {selectedClient?.CompanyName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <div>
              {modalMode === 'view' ? (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <h6>Company Information</h6>
                      <p><strong>Name:</strong> {selectedClient.CompanyName}</p>
                      <p><strong>Code:</strong> {selectedClient.CompanyCode}</p>
                      <p><strong>Industry:</strong> {selectedClient.Industry || 'N/A'}</p>
                      <p><strong>Description:</strong> {selectedClient.Description || 'N/A'}</p>
                    </Col>
                  </Row>

                  <hr />

                  <Row className="mb-3">
                    <Col md={6}>
                      <h6>Primary Contact</h6>
                      <p><strong>Name:</strong> {selectedClient.ContactName}</p>
                      <p><strong>Title:</strong> {selectedClient.ContactTitle || 'N/A'}</p>
                      <p><strong>Email:</strong> {selectedClient.ContactEmail}</p>
                      <p><strong>Phone:</strong> {selectedClient.ContactPhone || 'N/A'}</p>
                    </Col>
                    <Col md={6}>
                      <h6>Address</h6>
                      <p>
                        {selectedClient.Address1}
                        {selectedClient.Address2 && <>, {selectedClient.Address2}</>}
                        <br />
                        {selectedClient.City}, {selectedClient.State} {selectedClient.Zip}
                        <br />
                        {selectedClient.Country}
                      </p>
                    </Col>
                  </Row>

                  <hr />

                  <Row className="mb-3">
                    <Col md={6}>
                      <h6>Billing Contact</h6>
                      <p><strong>Name:</strong> {selectedClient.BillingContactName || 'N/A'}</p>
                      <p><strong>Email:</strong> {selectedClient.BillingContactEmail || 'N/A'}</p>
                      <p><strong>Phone:</strong> {selectedClient.BillingContactPhone || 'N/A'}</p>
                    </Col>
                    <Col md={6}>
                      <h6>Support Contact</h6>
                      <p><strong>Name:</strong> {selectedClient.SupportContactName || 'N/A'}</p>
                      <p><strong>Email:</strong> {selectedClient.SupportContactEmail || 'N/A'}</p>
                      <p><strong>Phone:</strong> {selectedClient.SupportContactPhone || 'N/A'}</p>
                    </Col>
                  </Row>

                  <hr />

                  <Row className="mb-3">
                    <Col md={6}>
                      <h6>Account Status</h6>
                      <p><strong>Status:</strong> {selectedClient.IsActive ? <span className="badge bg-success">Active</span> : <span className="badge bg-danger">Inactive</span>}</p>
                    </Col>
                  </Row>

                  <hr />

                  <p className="text-muted small">
                    <strong>Created:</strong> {new Date(selectedClient.CreatedDateTime).toLocaleString()}
                    <br />
                    <strong>Last Modified:</strong> {new Date(selectedClient.LastModifiedDateTime).toLocaleString()}
                  </p>
                </>
              ) : (
                <Form>
                  {/* Company Information Section */}
                  <h5 className="mb-3">Company Information</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.CompanyName || ''}
                          onChange={(e) => setEditData({ ...editData, CompanyName: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company Code</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.CompanyCode || ''}
                          onChange={(e) => setEditData({ ...editData, CompanyCode: e.target.value })}
                          disabled
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Industry</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.Industry || ''}
                          onChange={(e) => setEditData({ ...editData, Industry: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={editData.Description || ''}
                          onChange={(e) => setEditData({ ...editData, Description: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />

                  {/* Primary Contact Section */}
                  <h5 className="mb-3">Primary Contact</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.ContactName || ''}
                          onChange={(e) => setEditData({ ...editData, ContactName: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.ContactTitle || ''}
                          onChange={(e) => setEditData({ ...editData, ContactTitle: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={editData.ContactEmail || ''}
                          onChange={(e) => setEditData({ ...editData, ContactEmail: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          value={editData.ContactPhone || ''}
                          onChange={(e) => setEditData({ ...editData, ContactPhone: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />

                  {/* Address Section */}
                  <h5 className="mb-3">Address</h5>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 1</Form.Label>
                    <Form.Control
                      type="text"
                      value={editData.Address1 || ''}
                      onChange={(e) => setEditData({ ...editData, Address1: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 2</Form.Label>
                    <Form.Control
                      type="text"
                      value={editData.Address2 || ''}
                      onChange={(e) => setEditData({ ...editData, Address2: e.target.value })}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.City || ''}
                          onChange={(e) => setEditData({ ...editData, City: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>State/Province</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.State || ''}
                          onChange={(e) => setEditData({ ...editData, State: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.Zip || ''}
                          onChange={(e) => setEditData({ ...editData, Zip: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      type="text"
                      value={editData.Country || ''}
                      onChange={(e) => setEditData({ ...editData, Country: e.target.value })}
                    />
                  </Form.Group>

                  <hr />

                  {/* Billing Contact Section */}
                  <h5 className="mb-3">Billing Contact</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.BillingContactName || ''}
                          onChange={(e) => setEditData({ ...editData, BillingContactName: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={editData.BillingContactEmail || ''}
                          onChange={(e) => setEditData({ ...editData, BillingContactEmail: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      value={editData.BillingContactPhone || ''}
                      onChange={(e) => setEditData({ ...editData, BillingContactPhone: e.target.value })}
                    />
                  </Form.Group>

                  <hr />

                  {/* Support Contact Section */}
                  <h5 className="mb-3">Support Contact</h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editData.SupportContactName || ''}
                          onChange={(e) => setEditData({ ...editData, SupportContactName: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={editData.SupportContactEmail || ''}
                          onChange={(e) => setEditData({ ...editData, SupportContactEmail: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      value={editData.SupportContactPhone || ''}
                      onChange={(e) => setEditData({ ...editData, SupportContactPhone: e.target.value })}
                    />
                  </Form.Group>

                </Form>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          {modalMode === 'edit' && (
            <Button variant="primary" onClick={handleSaveChanges} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>


    </Container>
  );
};

export default ClientDashboard;
